import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Upload,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Context } from '../../context/context';
import CustomModal from '../../components/modal';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { disableRefetch } from '../../redux/slices/menu';

export default function GiftCardCategories() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setIsModalVisible } = useContext(Context);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [uploadLoading, setUploadLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [cardImageFile, setCardImageFile] = useState(null);
  const [cardImagePreview, setCardImagePreview] = useState(null);
  const { activeMenu } = useSelector((state) => state.menu, shallowEqual);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      console.log('Fetching categories from API...');
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/quickgift/category-list`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      // Format the data for display
      let formattedData = [];

      if (
        response.data &&
        response.data.status === 'success' &&
        Array.isArray(response.data.data)
      ) {
        const categoryData = response.data.data;
        formattedData = categoryData.map((category) => ({
          id: category.id,
          name: category.name,
          image: category.image,
          cardImage: category.card_image,
          created_at: category.created_at,
        }));
      } else {
        console.log('No categories found or invalid API response format');
      }

      console.log('Formatted data:', formattedData);
      setCategories(formattedData);
    } catch (error) {
      console.error('Error fetching gift card categories:', error);
      toast.error('Failed to fetch gift card categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (activeMenu.refetch) {
      fetchCategories();
      dispatch(disableRefetch(activeMenu));
    }
  }, [activeMenu.refetch]);

  const handleImageChange = (info) => {
    if (info.file.status === 'uploading') {
      return;
    }
    if (info.file.originFileObj) {
      // Store the file object directly
      setImageFile(info.file.originFileObj);

      // Generate preview
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(info.file.originFileObj);
    }
  };

  const handleCardImageChange = (info) => {
    if (info.file.status === 'uploading') {
      return;
    }
    if (info.file.originFileObj) {
      // Store the file object directly
      setCardImageFile(info.file.originFileObj);

      // Generate preview
      const reader = new FileReader();
      reader.onload = () => {
        setCardImagePreview(reader.result);
      };
      reader.readAsDataURL(info.file.originFileObj);
    }
  };

  const handleAddCategory = async () => {
    try {
      const values = await form.validateFields();

      if (!imageFile) {
        message.error('Please select a category image');
        return;
      }

      if (!cardImageFile) {
        message.error('Please select a card image');
        return;
      }

      setUploadLoading(true);

      const formData = new FormData();
      formData.append('cat_name', values.name);
      formData.append('cat_image', imageFile);
      formData.append('cat_cardImg', cardImageFile);

      // Log the FormData entries for debugging
      console.log('Form data entries:');
      for (let pair of formData.entries()) {
        console.log(
          pair[0] +
            ': ' +
            (pair[1] instanceof File ? `File: ${pair[1].name}` : pair[1])
        );
      }

      console.log('Adding new category...');

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/quickgift/add-category`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log('Add API response:', response.data);

      if (
        response.data.success ||
        response.data.message === 'Category added successfully' ||
        response.data.status === 'success'
      ) {
        toast.success('Category added successfully');
        setIsAddModalOpen(false);
        form.resetFields();
        setImageFile(null);
        setImagePreview(null);
        setCardImageFile(null);
        setCardImagePreview(null);
        fetchCategories();
      } else {
        toast.error(response.data.message || 'Failed to add category');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error(error.response?.data?.message || 'Failed to add category');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;

    try {
      console.log('Deleting category with ID:', selectedCategory.id);

      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/quickgift/delete-category/${selectedCategory.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      console.log('Delete API response:', response.data);

      if (
        response.data.success ||
        response.data.message === 'Category deleted successfully' ||
        response.data.status === 'success'
      ) {
        toast.success('Category deleted successfully');
        setIsDeleteModalOpen(false);
        fetchCategories();
      } else {
        toast.error(response.data.message || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.message || 'Failed to delete category');
    }
  };

  const columns = [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('category image'),
      dataIndex: 'image',
      key: 'image',
      render: (image) => (
        <img
          src={image}
          alt='Category'
          style={{
            width: 100,
            height: 100,
            objectFit: 'cover',
            borderRadius: '4px',
          }}
        />
      ),
    },
    {
      title: t('card image'),
      dataIndex: 'cardImage',
      key: 'cardImage',
      render: (image) =>
        image ? (
          <img
            src={image}
            alt='Card'
            style={{
              width: 100,
              height: 100,
              objectFit: 'cover',
              borderRadius: '4px',
            }}
          />
        ) : (
          <div
            style={{
              width: 100,
              height: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
              color: '#999',
              borderRadius: '4px',
            }}
          >
            No card image
          </div>
        ),
    },
    {
      title: t('created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: t('options'),
      key: 'options',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type='primary'
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedCategory(record);
              setIsEditModalOpen(true);
              form.setFieldsValue({
                name: record.name,
              });
              setImagePreview(record.image);
              setCardImagePreview(record.cardImage);
            }}
          >
            {t('edit')}
          </Button>
          <Button
            type='danger'
            icon={<DeleteOutlined />}
            onClick={() => {
              console.log('Delete button clicked for category ID:', record.id);
              setSelectedCategory(record);
              setIsDeleteModalOpen(true);
            }}
          >
            {t('delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const uploadProps = {
    name: 'cat_image',
    accept: 'image/jpeg,image/png,image/webp',
    beforeUpload: (file) => {
      const isJpgOrPng =
        file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
      if (!isJpgOrPng) {
        message.error('You can only upload JPG/PNG/WEBP file!');
        return Upload.LIST_IGNORE;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('Image must be smaller than 2MB!');
        return Upload.LIST_IGNORE;
      }

      // Store the file directly
      setImageFile(file);

      // Generate preview
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Return false to prevent antd's default upload behavior
      return false;
    },
    showUploadList: false,
  };

  const cardUploadProps = {
    name: 'cat_cardImg',
    accept: 'image/jpeg,image/png,image/webp',
    beforeUpload: (file) => {
      const isJpgOrPng =
        file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
      if (!isJpgOrPng) {
        message.error('You can only upload JPG/PNG/WEBP file!');
        return Upload.LIST_IGNORE;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('Image must be smaller than 2MB!');
        return Upload.LIST_IGNORE;
      }

      // Store the file directly
      setCardImageFile(file);

      // Generate preview
      const reader = new FileReader();
      reader.onload = () => {
        setCardImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Return false to prevent antd's default upload behavior
      return false;
    },
    showUploadList: false,
  };

  return (
    <Card
      title={t('Gift Card Categories')}
      extra={
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => {
            setIsAddModalOpen(true);
            form.resetFields();
            setImagePreview(null);
            setImageFile(null);
            setCardImagePreview(null);
            setCardImageFile(null);
          }}
        >
          {t('add.category')}
        </Button>
      }
    >
      {loading ? (
        <div
          className='d-flex justify-content-center align-items-center'
          style={{ height: '400px' }}
        >
          <Spin size='large' />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={categories}
          rowKey='id'
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          locale={{
            emptyText: 'No gift card categories found',
          }}
        />
      )}

      {/* Add Category Modal */}
      <Modal
        title={t('add.category')}
        open={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        footer={[
          <Button key='cancel' onClick={() => setIsAddModalOpen(false)}>
            {t('cancel')}
          </Button>,
          <Button
            key='submit'
            type='primary'
            loading={uploadLoading}
            onClick={handleAddCategory}
          >
            {t('save')}
          </Button>,
        ]}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='name'
            label={t('category.name')}
            rules={[{ required: true, message: t('required') }]}
          >
            <Input placeholder={t('enter.category.name')} />
          </Form.Item>

          <Form.Item
            label={t('category.image')}
            required
            help='Main category image'
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {imagePreview ? (
                <div style={{ marginBottom: 16 }}>
                  <img
                    src={imagePreview}
                    alt='Preview'
                    style={{
                      width: 200,
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              ) : null}

              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>{t('upload.image')}</Button>
              </Upload>
            </div>
          </Form.Item>

          <Form.Item
            label={t('card.image')}
            required
            help='Image displayed on gift cards'
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {cardImagePreview ? (
                <div style={{ marginBottom: 16 }}>
                  <img
                    src={cardImagePreview}
                    alt='Card Preview'
                    style={{
                      width: 200,
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              ) : null}

              <Upload {...cardUploadProps}>
                <Button icon={<UploadOutlined />}>
                  {t('upload.card.image')}
                </Button>
              </Upload>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Category Modal */}
      <Modal
        title={t('edit.category')}
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={[
          <Button key='cancel' onClick={() => setIsEditModalOpen(false)}>
            {t('cancel')}
          </Button>,
          <Button
            key='submit'
            type='primary'
            loading={uploadLoading}
            onClick={async () => {
              try {
                const values = await form.validateFields();

                setUploadLoading(true);

                const formData = new FormData();
                if (values.name) {
                  formData.append('cat_name', values.name);
                }
                if (imageFile) {
                  formData.append('cat_image', imageFile);
                }
                if (cardImageFile) {
                  formData.append('cat_cardImg', cardImageFile);
                }

                // Log the FormData entries for debugging
                console.log('Edit form data entries:');
                for (let pair of formData.entries()) {
                  console.log(
                    pair[0] +
                      ': ' +
                      (pair[1] instanceof File
                        ? `File: ${pair[1].name}`
                        : pair[1])
                  );
                }

                console.log('Updating category with ID:', selectedCategory.id);

                const response = await axios.post(
                  `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/quickgift/update-category/${selectedCategory.id}`,
                  formData,
                  {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem('token')}`,
                      'Content-Type': 'multipart/form-data',
                    },
                  }
                );

                console.log('Update API response:', response.data);

                if (
                  response.data.success ||
                  response.data.message === 'Category updated successfully' ||
                  response.data.status === 'success'
                ) {
                  toast.success('Category updated successfully');
                  setIsEditModalOpen(false);
                  setImageFile(null);
                  setImagePreview(null);
                  setCardImageFile(null);
                  setCardImagePreview(null);
                  fetchCategories();
                } else {
                  toast.error(
                    response.data.message || 'Failed to update category'
                  );
                }
              } catch (error) {
                console.error('Error updating category:', error);
                toast.error(
                  error.response?.data?.message || 'Failed to update category'
                );
              } finally {
                setUploadLoading(false);
              }
            }}
          >
            {t('update')}
          </Button>,
        ]}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='name'
            label={t('category.name')}
            rules={[{ required: true, message: t('required') }]}
          >
            <Input placeholder={t('enter.category.name')} />
          </Form.Item>

          <Form.Item label={t('category.image')} help='Main category image'>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {imagePreview ? (
                <div style={{ marginBottom: 16 }}>
                  <img
                    src={imagePreview}
                    alt='Preview'
                    style={{
                      width: 200,
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              ) : null}

              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>{t('change.image')}</Button>
              </Upload>
            </div>
          </Form.Item>

          <Form.Item
            label={t('card.image')}
            help='Image displayed on gift cards'
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {cardImagePreview ? (
                <div style={{ marginBottom: 16 }}>
                  <img
                    src={cardImagePreview}
                    alt='Card Preview'
                    style={{
                      width: 200,
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: '4px',
                    }}
                  />
                </div>
              ) : null}

              <Upload {...cardUploadProps}>
                <Button icon={<UploadOutlined />}>
                  {t('change.card.image')}
                </Button>
              </Upload>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title={t('delete.category')}
        open={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        footer={[
          <Button key='cancel' onClick={() => setIsDeleteModalOpen(false)}>
            {t('cancel')}
          </Button>,
          <Button
            key='submit'
            type='primary'
            loading={uploadLoading}
            onClick={handleDeleteCategory}
          >
            {t('delete')}
          </Button>,
        ]}
      >
        <p>{t('are.you.sure.delete')}</p>
      </Modal>
    </Card>
  );
}
